import { useMemo } from 'react';

import { ProfileTabKey } from '@/contents/user-profile/content';
import useBalance from '@/hooks/useBalance';
import { TokenDenom, TokenId, useTokenPrices } from '@/hooks/useTokenPrices';
import { useAuth } from '@/provider/UserProvider';
import { formatCash, getUsdWithToken } from '@/utils';
import { currencyFormatter } from '@/utils/currency';

const useUserInfo = () => {
  const { auth } = useAuth();
  const { user } = auth;
  const { data: prices } = useTokenPrices();
  const { usd, orai, airi } = useBalance([TokenDenom.AIRI, TokenDenom.ORAI, TokenDenom.USDT]);

  const oraiToUsd = useMemo(
    () =>
      getUsdWithToken({
        amount: orai,
        tokenId: TokenId.ORAI,
        prices: prices || {},
      }),
    [prices, orai],
  );

  const airiToUsd = useMemo(
    () =>
      getUsdWithToken({
        amount: airi,
        tokenId: TokenId.AIRI,
        prices: prices || {},
      }),
    [prices, airi],
  );

  const usdtToUsd = useMemo(
    () =>
      getUsdWithToken({
        amount: usd,
        tokenId: TokenId.USDT,
        prices: prices || {},
      }),
    [prices, usd],
  );

  const listLink = [
    {
      key: ProfileTabKey.template,
      link: `/profile/${user.publicAddress}?tab=${ProfileTabKey.template}`,
      text: 'MY POSTS',
    },
    {
      key: ProfileTabKey.processing,
      link: `/profile/${user.publicAddress}?tab=${ProfileTabKey.processing}`,
      text: 'PRIVATE ARTWORK',
    },
    {
      key: ProfileTabKey.apikey,
      link: '/api-key',
      text: 'API Keys',
    },
  ];

  const listToken = [
    {
      key: TokenDenom.ORAI,
      text: 'ORAI',
      amount: formatCash(orai * 10 ** -6, 1),
      toUsd: formatCash(oraiToUsd * 10 ** -6, 1),
    },
    {
      key: TokenDenom.AIRI,
      text: 'AIRI',
      amount: formatCash(airi * 10 ** -6, 1),
      toUsd: currencyFormatter(airiToUsd, { significantDigits: 1 }),
    },
    {
      key: TokenDenom.USDT,
      text: 'USDT',
      amount: formatCash(usd * 10 ** -6, 1),
      toUsd: formatCash(usdtToUsd * 10 ** -6, 1),
    },
  ];

  return { listLink, listToken };
};

export default useUserInfo;
